import Vue from 'vue';
import _ from '../../utils';

export type MediaType = 'mobile' | 'tablet' | 'desktop';
export type ResizeEventType = (mediaType: MediaType, width: number, height: number) => void;

export default class Media {
  private reactiveVars = Vue.observable({
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isOrientationPortrait: false,
    isOrientationLandscape: false,
  });
  /** Mobile excludes tablets and desktops  */
  public get isMobile() { return this.reactiveVars.isMobile; }
  /** Tablet excludes mobiles and desktops */
  public get isTablet() { return this.reactiveVars.isTablet; }
  /** Desktop excludes tablets and mobiles */
  public get isDesktop() { return this.reactiveVars.isDesktop; }
  /** Not-mobile = Tablets and desktops */
  public get isNotMobile() { return this.isTablet || this.isDesktop; }
  public get isOrientationPortrait() { return this.reactiveVars.isOrientationPortrait; }
  public get isOrientationLandscape() { return this.reactiveVars.isOrientationLandscape; }

  private resizeListeners = [] as ResizeEventType[];

  constructor() {
    // This assumes that there will only be one main instance of this class that is accessed via Vue.$media
    // otherwise for every instance there will be a new subscription to window.resize
    const debouncedResizeHandler = Vue.$debounce(this.onWindowResize.bind(this), 100);
    if (window) {
      window.addEventListener('resize', debouncedResizeHandler);
      window.addEventListener('load', () => {
        setTimeout(() => {
          this.onWindowResize();
        }, 50);
      });
    }
    this.onWindowResize();
  }

  /** @deprecated Use addResizeListener instead */
  public addResizeListeners(fn: ResizeEventType) { this.addResizeListener(fn); }
  public addResizeListener(fn: ResizeEventType) {
    if (this.resizeListeners.includes(fn)) {
      // Adding two listeners can be considered a mistake in any case
      return;
    }
    this.resizeListeners.push(fn);
    this.onWindowResize();
  }

  /** @deprecated Use removeResizeListener instead */
  public removeResizeListeners(fn: ResizeEventType) { this.removeResizeListener(fn); }
  public removeResizeListener(fnToRemove: ResizeEventType) {
    this.resizeListeners = this.resizeListeners.filter((fn) => fn !== fnToRemove);
  }

  private onWindowResize() {
    // Get rid of orphaned listeners
    this.resizeListeners = this.resizeListeners.filter((fn) => typeof fn === 'function');
    let mediaType: MediaType = 'desktop';
    const appEl = _.component.getAppEl()!;
    const appRect = appEl?.getBoundingClientRect() ?? { width: window?.innerWidth, height: window?.innerHeight };

    // Make sure this matches the breakpoints within sass
    Vue.set(this.reactiveVars, 'isDesktop', appRect.width >= 900);
    Vue.set(this.reactiveVars, 'isTablet', appRect.width >= 600 && appRect.width < 900);
    Vue.set(this.reactiveVars, 'isMobile', appRect.width < 600);

    if (window && window.matchMedia) {
      Vue.set(this.reactiveVars, 'isOrientationLandscape', window.matchMedia('(orientation: landscape)').matches);
      Vue.set(this.reactiveVars, 'isOrientationPortrait', window.matchMedia('(orientation: portrait)').matches);
    }

    if (this.isDesktop) {
      mediaType = 'desktop';
    } else if (this.isTablet) {
      mediaType = 'tablet';
    } else {
      mediaType = 'mobile';
    }
    this.resizeListeners.forEach((fn) => {
      fn(mediaType, appRect.width, appRect.height);
    });
  }
}
